/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback } from "react";
import { useHistory } from "react-router-dom";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import { IconButton, CircularProgress } from "@material-ui/core";
import { useSelector } from "react-redux";
import cogoToast from "cogo-toast";
import {
  WhatsappShareButton,
  WhatsappIcon,
  LinkedinShareButton,
  LinkedinIcon,
} from "react-share";
import { getReferralOpportunitiesAction } from "../../../../../redux/actions/jobActions";
import { encodeShortPayload } from "../../../ShortRedirect";
import icons from "../../../../../assets/icons/icon-collection.svg";
import "./PostApplicationReferralModal.scss";

const PostApplicationReferralModal = ({ open, onClose, excludeJobId }) => {
  const history = useHistory();
  const { userData } = useSelector(({ auth }) => auth);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getReferralOpportunitiesAction(excludeJobId)
      .then((data) => setJobs(data.slice(0, 10)))
      .finally(() => setLoading(false));
  }, [open]);

  const buildReferralUrl = useCallback(
    (job) => {
      const baseUrl = process.env.REACT_APP_WEB_URL || window.location.origin;
      const code = encodeShortPayload({
        jobId: job.id,
        username: userData.username,
        userId: userData.id,
      });
      return `${baseUrl}/r/${code}`;
    },
    [userData.username, userData.id]
  );

  const copyLink = (job) => {
    const url = buildReferralUrl(job);
    navigator.clipboard
      .writeText(url)
      .then(() => {
        cogoToast.success("Link copied!", {
          hideAfter: 5,
          position: "top-center",
        });
      })
      .catch(() => {
        cogoToast.error("Could not copy link", {
          hideAfter: 4,
          position: "top-center",
        });
      });
  };

  const openJobDetail = (job) => {
    history.push(`/home/${job.id}`);
    onClose();
  };

  const tier3Jobs = jobs.filter((j) => j.job_tier === 3);
  const tier2Jobs = jobs.filter((j) => j.job_tier === 2);
  const featuredTier3 = tier3Jobs.slice(0, 3);
  const featuredFill = Math.max(0, 3 - featuredTier3.length);
  const featuredTier2 = tier2Jobs.slice(0, featuredFill);
  const featuredJobs = [...featuredTier3, ...featuredTier2];
  const moreJobs = [
    ...tier3Jobs.slice(featuredTier3.length),
    ...tier2Jobs.slice(featuredTier2.length),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby="referral-opportunities-dialog"
      style={{ zIndex: 1400 }}
      PaperProps={{
        className: "par-dialog-paper",
      }}
    >
      {/* Header */}
      <div className="par-header">
        <div className="par-header-top">
          <div className="par-header-title-row">
            <div className="par-header-icon">
              <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="11" cy="11" r="11" fill="#3A6ED4"/>
                <path d="M6 11.5L9.5 15L16 8" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <h2 className="par-title">Application Successful!</h2>
            </div>
          </div>
          <IconButton onClick={onClose} size="small">
            <svg width="20" height="20">
              <use href={`${icons}#close`}></use>
            </svg>
          </IconButton>
        </div>

        <p className="par-subtitle">
          Now it&apos;s time to help your network level up (and get rewarded for it).
        </p>
        <p className="par-description">
          Refer top talent to active roles on workbrook. Earn 3% annual salary
          for every successful candidate you refer.
        </p>
      </div>

      <DialogContent style={{ padding: 0 }}>
        {loading && (
          <div className="par-loading">
            <CircularProgress size={32} style={{ color: "#3A6ED4" }} />
          </div>
        )}

        {!loading && jobs.length === 0 && (
          <p className="par-empty-state">
            No referral opportunities available right now. Check back soon!
          </p>
        )}

        {!loading && jobs.length > 0 && (
          <>
            {/* Featured jobs (tier 3 or first 2) with share buttons */}
            {featuredJobs.map((job) => (
              <div key={job.id} className="par-featured-job">
                <div className="par-job-info">
                  <button
                    onClick={() => openJobDetail(job)}
                    className="par-job-info-btn"
                  >
                    <p className="par-job-title">{job.role_title}</p>
                    <p className="par-job-meta">
                      {[job.campany_name, job.location]
                        .filter(Boolean)
                        .join(" • ")}
                    </p>
                  </button>
                </div>
                <div className="par-share-actions">
                  <WhatsappShareButton
                    url={buildReferralUrl(job)}
                    title={`Job opportunity: ${job.role_title}${job.campany_name ? ` at ${job.campany_name}` : ""}`}
                  >
                    <WhatsappIcon size={32} round />
                  </WhatsappShareButton>
                  <LinkedinShareButton url={buildReferralUrl(job)}>
                    <LinkedinIcon size={32} round />
                  </LinkedinShareButton>
                  <button
                    onClick={() => copyLink(job)}
                    className="par-copy-btn"
                  >
                    <svg width="14" height="14">
                      <use href={`${icons}#link`}></use>
                    </svg>
                    Copy Link & Earn
                  </button>
                </div>
              </div>
            ))}

            {/* More jobs — simple list with Share Link */}
            {moreJobs.length > 0 && (
              <>
                <p className="par-more-jobs-hint">
                  View more open roles matching your network
                </p>
                {moreJobs.map((job) => (
                  <div key={job.id} className="par-more-job">
                    <button
                      onClick={() => openJobDetail(job)}
                      className="par-more-job-btn"
                    >
                      <p className="par-more-job-title">{job.role_title}</p>
                      <p className="par-more-job-meta">
                        {[job.campany_name, job.location]
                          .filter(Boolean)
                          .join(" • ")}
                      </p>
                    </button>
                    <button
                      onClick={() => copyLink(job)}
                      className="par-share-link-btn"
                    >
                      Share Link
                      <span style={{ fontSize: "16px" }}>›</span>
                    </button>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </DialogContent>

      {/* Footer */}
      <div className="par-footer">
        <button
          onClick={() => history.push("/insights/referrals")}
          className="par-referrals-link"
        >
          <svg width="16" height="16">
            <use href={`${icons}#referrals`}></use>
          </svg>
          See successful referrals
        </button>
        <div className="par-footer-actions">
          <button onClick={onClose} className="par-btn-close">
            Close
          </button>
          <button
            onClick={() => {
              history.push("/insights");
              onClose();
            }}
            className="par-btn-insights"
          >
            Go to Insights
          </button>
        </div>
      </div>
    </Dialog>
  );
};

export default PostApplicationReferralModal;
